'use client';

import classNames from 'classnames';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import LottieJumpLoading from '@/components/chat-app/components/LottieJumpLoading';
import type { ICallChatContext } from '@/context/HomeContext';
import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import useStorage from '@/hooks/useStorage';
import { useTranslation } from '@/i18n/client';
import type { ICallMessageDetail, ICallMessageItem } from '@/service/call';
import { getCallMessages } from '@/service/call';

import BackIcon from '../HomePage/BackIcon';
import AIMessage from './components/AIMessage';
import UserMessage from './components/UserMessage';
import styles from './index.less';

interface IProps {
  context: ICallChatContext;
  className?: string;
  forceInMobile?: boolean;
}

const CallChatPage = memo((props: IProps) => {
  const { context, className, forceInMobile } = props;
  const { sessionId, callerName, callerAvatar, assistantAvatar } = context;
  const [layoutSize] = useStorage('layoutSize');
  const [isClient, setIsClient] = useState(false);

  const inMobile = useMemo(
    () =>
      typeof forceInMobile === 'boolean'
        ? forceInMobile
        : isClient && layoutSize === LAYOUT_SIZE.SMALL,
    [layoutSize, isClient, forceInMobile]
  );

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<ICallMessageDetail | null>(null);

  const fetchMessages = useCallback(() => {
    if (!sessionId) return;

    setLoading(true);
    getCallMessages({ sessionId })
      .then((res) => {
        if (res.data.code === 0) {
          setDetail(res.data.data);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch call messages:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [sessionId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const displayName = detail?.caller?.isAnonymous
    ? t('rtcCall.anonymousUser')
    : detail?.caller?.username || callerName;

  const userAvatar = detail?.caller?.avatar || callerAvatar;
  const aiAvatar = detail?.assistant?.avatar || assistantAvatar;

  const renderMessage = (message: ICallMessageItem, index: number) => {
    const isAI = message.role === 'assistant';

    if (isAI) {
      return (
        <AIMessage
          key={message.messageId || index}
          avatar={aiAvatar}
          content={message.content || ''}
        />
      );
    }

    return (
      <UserMessage
        key={message.messageId || index}
        avatar={userAvatar}
        content={message.content || ''}
        displayName={displayName}
      />
    );
  };

  return (
    <div className={classNames(styles.container, className)}>
      {/* Header */}
      <div className={classNames(styles.headerContainer, { [styles.inMobile]: inMobile })}>
        <BackIcon className={styles.voiceIcon} iconClassName="!text-[#FEFDFF]" />
        <span className={styles.headerTitle}>{displayName}</span>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Summary card */}
        {detail?.summary && (
          <div className={styles.summaryCard}>
            <p className={styles.summaryText}>
              {t('call.chat.summary')}: {detail.summary}
            </p>
          </div>
        )}

        {/* Messages */}
        <div className={styles.messageList}>
          {loading ? (
            <div className={styles.loadingWrap}>
              <LottieJumpLoading className={styles.loadingIcon} />
            </div>
          ) : detail?.messages && detail.messages.length > 0 ? (
            detail.messages.map((msg, idx) => renderMessage(msg, idx))
          ) : (
            <div className={styles.emptyText}>{t('call.chat.noMessages')}</div>
          )}
        </div>
      </div>
    </div>
  );
});

CallChatPage.displayName = 'CallChatPage';

export default CallChatPage;
